import React, { useMemo, useState } from 'react';
import { Icon, Modal as MuiModal, Tooltip } from '@mui/material';
import styles from './FileViewerModal.module.scss';
import { Text } from '../../typography';
import classNames from 'classnames';
import PdfViewer from './PdfViewer';
import { fileType } from '../../../constants/misc';
import ImageViewer from './ImageViewer';
import EmptyViewer from './EmptyViewer';
import { downloadFile } from '../../../utilities/inspection';
import format from 'date-fns/format';
import TextWithIcon from '../../typography/TextWithIcon';
import Spacer from '../../typography/Spacer';
import { fileTypesToShow } from '../../../utilities/files';
import { modals } from '../../../providers/modals';
import { useModal } from '../../../providers/ModalProvider';
import { customColors, vehicleString } from '../../../utilities';
import constants from '../../../styles/contants.module.scss';
import { closeFileViewer } from '../../../graphql/cache/modal';
import { shareAnyFile } from '../../../utilities/pdf_export/pdfSearch';

export default function FileViewerModal({
  file,
  show = false,
  onClose,
  onDetailsClick,
}) {
  const url = file?.url || file?.imageUrl;
  const extension = file?.extension || file?.fileType || url?.split('.')?.pop();
  const [showDetails, setShowDetails] = useState(false);
  const { openModal } = useModal();

  const Viewer = useMemo(() => {
    switch (file?.fileType || file?.extension || extension) {
      case fileType.png:
      case 'png':
      case 'jpg':
      case fileType.jpeg:
      case 'jpeg':
      case fileType.webp:
      case 'webp':
      case fileType.gif:
      case 'gif':
        return <ImageViewer file={file} />;
      case fileType.pdf:
      case 'pdf':
        return <PdfViewer file={file} />;
      default:
        return (
          <EmptyViewer
            file={file}
            onClose={onClose}
            onDetailsClick={onDetailsClick}
          />
        );
    }
  }, [file, onClose, onDetailsClick, extension]);

  return (
    <MuiModal open={!!show && !!file} onClose={onClose} disableAutoFocus>
      <div className={styles.background}>
        {!file ? null : (
          <div
            className={classNames(
              styles.container,
              show ? styles.showContainer : styles.hideContainer,
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.header}>
              <Text truncate size="lg" weight="semiBold" noMargin>
                {file?.name}
              </Text>
              <div className={styles.icons}>
                {!!file?.downloadAllowed && (
                  <div className={styles.optionIcons}>
                    <Tooltip title="Share">
                      <Icon
                        onClick={() =>
                          shareAnyFile({
                            src: url,
                            fileName: file?.name || file?.title,
                          })
                        }
                        hover
                        style={{ color: customColors.BLUE, fontSize: '1.3rem' }}
                      >
                        ios_share
                      </Icon>
                    </Tooltip>
                    <Tooltip title="Open in New Window">
                      <Icon
                        hover
                        style={{ color: customColors.BLUE }}
                        onClick={() => window.open(url)}
                      >
                        open_in_new_window
                      </Icon>
                    </Tooltip>
                    <Tooltip title="Download">
                      <Icon
                        className={styles.download}
                        onClick={() =>
                          downloadFile(
                            file?.url || file?.imageUrl,
                            file?.name ||
                              `${format(
                                new Date(),
                                'yyyy-MM-dd HH:mm',
                              )}-Attachment`,
                          )
                        }
                      >
                        download
                      </Icon>
                    </Tooltip>
                  </div>
                )}
                <Icon className={styles.closeIcon} onClick={onClose}>
                  close
                </Icon>
              </div>
            </div>
            <Spacer />
            <div className={constants.header}>
              <TextWithIcon
                textAlign="right"
                hover
                icon={showDetails ? 'expand_less' : 'expand_more'}
                noMargin
                size="sm"
                color="accentPrimary"
                weight="semiBold"
                onClick={() => setShowDetails(!showDetails)}
              >
                {`${showDetails ? 'Hide' : 'Show'} Details`}
              </TextWithIcon>
              {showDetails && file?.__typename !== 'ObservationImage' && (
                <Icon
                  style={{ cursor: 'pointer' }}
                  baseClassName="material-icons-outlined"
                  onClick={() =>
                    openModal({
                      modalName: modals.fileUpload,
                      variables: {
                        fileToEdit: file,
                        modalTitle: 'Edit File',
                        onClose: () => closeFileViewer(),
                      },
                    })
                  }
                >
                  edit
                </Icon>
              )}
            </div>
            <Spacer />
            {showDetails ? (
              <div className={styles.detailsContainer}>
                <Text noMargin weight="semiBold">
                  Name
                </Text>
                <Text>{file?.name}</Text>
                {file?.__typename === 'ObservationImage' ||
                file?.__typename === 'IncidentImage' ? (
                  <div>
                    <Text noMargin weight="semiBold">
                      Description
                    </Text>
                    <Text noMargin>{file?.description || 'None'}</Text>
                  </div>
                ) : (
                  <div className={styles.details}>
                    {Object.entries(fileTypesToShow)?.map(([key, value]) => {
                      const fileValue = file[key] || null;
                      return (
                        <div className={styles.pair} key={key}>
                          <Text weight="semiBold" noMargin>
                            {value}
                          </Text>
                          <Text noMargin>
                            {fileValue === null
                              ? 'None'
                              : typeof fileValue === 'object' && value !== null
                              ? !!fileValue?.firstName
                                ? `${fileValue.firstName} ${fileValue.lastName}`
                                : !!fileValue?.title
                                ? fileValue.title
                                : vehicleString(fileValue)
                              : typeof fileValue === 'boolean'
                              ? fileValue === true
                                ? 'Yes'
                                : 'No'
                              : String(fileValue)}
                          </Text>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}
            {Viewer}
            <br />
          </div>
        )}
      </div>
    </MuiModal>
  );
}
